import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LinkIcon, 
  CurrencyDollarIcon, 
  UserGroupIcon,
  ChartBarIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { linksService } from '../../services/api';
import toast from 'react-hot-toast';

function HomePage() {
  const [url, setUrl] = useState('');
  const [isShortening, setIsShortening] = useState(false);
  const [shortenedUrl, setShortenedUrl] = useState(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleShortenUrl = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Link kısaltmak için giriş yapmalısınız');
      navigate('/login');
      return;
    }

    if (!url.trim()) {
      toast.error('Lütfen geçerli bir URL girin');
      return;
    }

    setIsShortening(true);
    try {
      const response = await linksService.create({ originalUrl: url });
      
      if (response.data.success) {
        setShortenedUrl(response.data.data);
        setUrl('');
        toast.success('Link başarıyla kısaltıldı!');
      }
    } catch (error) {
      toast.error('Link kısaltılırken bir hata oluştu');
    } finally {
      setIsShortening(false);
    }
  };

  const features = [
    {
      icon: LinkIcon,
      title: 'Hızlı Link Kısaltma',
      description: 'Uzun linklerinizi anında kısaltın ve kolayca paylaşın'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Para Kazanın',
      description: 'Her link tıklaması ile ülke bazlı oranlar üzerinden kazanç elde edin'
    },
    {
      icon: UserGroupIcon,
      title: 'Referans Sistemi',
      description: 'Arkadaşlarınızı davet edin ve onların kazançlarından komisyon alın'
    },
    {
      icon: ChartBarIcon,
      title: 'Detaylı İstatistikler',
      description: 'Linklerinizin performansını analiz edin ve kazançlarınızı takip edin'
    }
  ];

  const stats = [
    { label: 'Toplam Kullanıcı', value: '10,000+' },
    { label: 'Kısaltılan Link', value: '50M+' },
    { label: 'Ödenen Tutar', value: '$250K+' },
    { label: 'Aktif Ülke', value: '150+' }
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link kopyalandı!');
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-purple-800 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Linklerini Kısalt,
              <span className="block text-yellow-300">Para Kazan!</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Her link tıklaması ile para kazanan gelişmiş link kısaltma servisi. 
              Ülke bazlı yüksek CPM oranları ve güvenilir ödeme sistemi.
            </p>

            {/* Link Shortener Form */}
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleShortenUrl} className="flex flex-col sm:flex-row gap-4">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Kısaltmak istediğiniz URL'i girin..."
                  className="flex-1 px-4 py-3 text-lg rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                  disabled={isShortening}
                />
                <button
                  type="submit"
                  disabled={isShortening || !url.trim()}
                  className="btn btn-warning btn-lg whitespace-nowrap font-semibold"
                >
                  {isShortening ? 'Kısaltılıyor...' : 'Linkini Kısalt'}
                </button>
              </form>

              {/* Shortened URL Result */}
              {shortenedUrl && (
                <div className="mt-6 p-4 bg-white rounded-lg shadow-lg animate-fade-in">
                  <p className="text-sm text-gray-600 mb-2">Kısaltılmış linkiniz:</p>
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <span className="text-primary-600 font-medium flex-1 truncate">
                      {shortenedUrl.shortUrl}
                    </span>
                    <button
                      onClick={() => copyToClipboard(shortenedUrl.shortUrl)}
                      className="ml-2 btn btn-primary btn-sm"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                      Kopyala
                    </button>
                  </div>
                </div>
              )}

              {!isAuthenticated && (
                <p className="mt-4 text-blue-100 text-sm">
                  💡 Daha fazla özellik için{' '}
                  <Link to="/register" className="text-yellow-300 hover:text-yellow-200 underline">
                    ücretsiz hesap oluşturun
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden KsLnk?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sadece link kısaltma değil, aynı zamanda para kazanma platformu
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="text-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nasıl Çalışır?
            </h2>
            <p className="text-xl text-gray-600">
              3 basit adımda para kazanmaya başlayın
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Hesap Oluşturun
              </h3>
              <p className="text-gray-600">
                Ücretsiz hesabınızı oluşturun ve hemen kazanmaya başlayın
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Linklerinizi Kısaltın
              </h3>
              <p className="text-gray-600">
                Uzun linklerinizi kısaltın ve sosyal medyada paylaşın
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Para Kazanın
              </h3>
              <p className="text-gray-600">
                Her tıklama ile ülkenize göre para kazanın ve PayPal ile çekin
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-purple-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Hemen Başlayın!
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Hesabınızı oluşturun ve linklerinizle para kazanmaya başlayın
          </p>
          {!isAuthenticated ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn btn-warning btn-xl">
                Ücretsiz Hesap Oluştur
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/pricing" className="btn btn-ghost btn-xl text-white border-white hover:bg-white hover:text-primary-600">
                Kazanç Oranları
                <EyeIcon className="w-5 h-5 ml-2" />
              </Link>
            </div>
          ) : (
            <Link to="/dashboard" className="btn btn-warning btn-xl">
              Panele Git
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

export default HomePage;